import React, { useEffect, useRef } from "react";
import "./App.css";
import howToGif from "./assets/how-to.gif";
import { applyDocumentLocale, getInitialLocale, normaliseLocale, withLocaleParam } from "./i18n/locale";
import { translate } from "./i18n/messages";
import { languageFromEvent } from "./i18n/quizPlayerMessage";

const DEFAULT_QUIZ_UUID = "a20a8c25-6a51-42ea-9543-303a96ffb0ac";
const MIN_ANSWER_INTERVAL = 50;

const extractUUID = (url: string) => {
    const matches = url.match(
        /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/
    );
    return matches ? matches[0] : null;
};

const getGameTargetOrigin = () => {
    if (
        window.location.host.includes("localhost") ||
        window.location.host.includes("test-")
    ) {
        return "https://test-sorcerer-game.quizalize.com";
    }

    return "https://sorcerer-game.quizalize.com";
};

const getGameFrameUrl = (locale: string) => {
    return withLocaleParam(`${getGameTargetOrigin()}/`, locale);
};

const getQuizPlayerUrlFromParentUrl = (locale: string) => {
    const quizUuid = extractUUID(window.location.href) || DEFAULT_QUIZ_UUID;
    const token = new URLSearchParams(window.location.search).get("token");
    const quizMode = new URLSearchParams(window.location.search).get("quizMode");

    let quizPlayerOrigin = "https://test-player.quizalize.com";
    if (
        window.location.host.includes("quizalize") ||
        window.location.host.includes("the-sorcerer-wrapper.")
    ) {
        quizPlayerOrigin = "https://player.quizalize.com";
    }
    if (
        window.location.host.includes("test-") ||
        window.location.host.includes("the-sorcerer-wrapper-test")
    ) {
        quizPlayerOrigin = "https://test-player.quizalize.com";
    }

    // localhost dev embeds the locally-running quiz-player (:3005); test/prod above use
    // the test/prod player (environment-aware player origin).
    if (window.location.host.includes("localhost")) {
        quizPlayerOrigin = "http://localhost:3005";
    }

    const url = new URL(`/quiz/${quizUuid}`, quizPlayerOrigin);
    if (token) {
        url.searchParams.set("token", token);
    }
    url.searchParams.set("origin", window.location.origin);
    if (quizMode) {
        url.searchParams.set("quizMode", quizMode);
    }
    url.searchParams.set("language", locale);

    return url.toString();
};

function App() {
    const localeRef = useRef(getInitialLocale());
    const locale = localeRef.current;
    const t = (key: string, values?: Record<string, string | number>) =>
        translate(locale, key, values);

    const quizPlayerIframeRef = useRef<HTMLIFrameElement>(null);
    const gameIframeRef = useRef<HTMLIFrameElement>(null);
    const quizPlayerContainerRef = useRef<HTMLDivElement>(null);
    const lastAnswerTime = useRef(0);

    const [ammo, setAmmo] = React.useState(5);
    const [numQuizQuestions, setNumQuizQuestions] = React.useState<
        number | null
    >(null);
    const [numQuizQuestionsAnswered, setNumQuizQuestionsAnswered] =
        React.useState(0);
    const [quizFinished, setQuizFinished] = React.useState(false);
    const [quizPlayerUrl, setQuizPlayerUrl] = React.useState(() =>
        getQuizPlayerUrlFromParentUrl(locale)
    );
    // Bumped to re-mount the quiz-player iframe on "Play Again" (restarts the quiz from Q1 with no
    // page reload).
    const [quizPlayerKey, setQuizPlayerKey] = React.useState(0);
    const [gameState, setGameState] = React.useState<
        "NOT_STARTED" | "ACTIVE" | "PAUSED" | "WON" | "LOST"
    >("NOT_STARTED");
    const [showQuizMobile, setShowQuizMobile] = React.useState(true);
    const [showHowToPlay, setShowHowToPlay] = React.useState(false);
    // Bumped when the embedded player tells us IT switched language, to re-render wrapper text via t().
    const [, setLangVersion] = React.useState(0);

    // Set once at launch (like quizPlayerUrl) so a runtime language change re-renders the React
    // wrapper UI via t() WITHOUT reloading the nested game iframe (the static game has no runtime
    // language API — syncing it is a separate follow-up).
    const [gameFrameUrl] = React.useState(() => getGameFrameUrl(locale));
    const gameTargetOrigin = getGameTargetOrigin();

    useEffect(() => {
        applyDocumentLocale(localeRef.current);
    }, []);

    useEffect(() => {
        const handleMessage = (event: MessageEvent) => {
            console.log("App.tsx message", event.data);

            // Player switched its own language in-place (HotSwapping) and notified us — follow it in
            // the React wrapper UI (re-render via t()). quizPlayerUrl + gameFrameUrl are set once, so
            // neither iframe reloads (the running quiz/game isn't reset). The nested static game keeps
            // its launch language (separate follow-up).
            const nextLanguage = languageFromEvent(event.data, localeRef.current);
            if (nextLanguage) {
                const normalised = normaliseLocale(nextLanguage);
                if (normalised !== localeRef.current) {
                    localeRef.current = normalised;
                    applyDocumentLocale(normalised);
                    // Keep the address bar honest: swap the stale launch ?language= for the chosen
                    // language so the URL matches what's shown (and a copied/shared URL opens in it).
                    // replaceState → no navigation/reload, so neither iframe is reset.
                    if (
                        typeof window !== "undefined" &&
                        window.history &&
                        typeof window.history.replaceState === "function"
                    ) {
                        window.history.replaceState(
                            window.history.state,
                            "",
                            withLocaleParam(window.location.href, normalised)
                        );
                    }
                    setLangVersion((v) => v + 1);
                }
            }

            if (event.data?.eventType === "START_LEVEL") {
                setGameState("ACTIVE");
            }
            if (event.data?.eventType === "SHOOT") {
                const { newAmmo } = event.data;
                setAmmo(newAmmo);
            }
            if (event.data?.eventType === "PAUSE") {
                console.log("App.tsx PAUSE");
                setShowQuizMobile(true);
                setGameState("PAUSED");
            }
            if (event.data?.eventType === "FINISHED") {
                setQuizFinished(true);
            }
            if (event.data?.eventType === "UPDATE_HIGH_SCORE") {
                const { score, quizUuid } = event.data;

                const highScoresStr = localStorage.getItem("high_scores") || "";
                const highScoresJSON: {
                    [quizUuid: string]: number;
                } = highScoresStr.length > 0 ? JSON.parse(highScoresStr) : {};

                const currentHighScore = highScoresJSON[quizUuid] || 0;
                const isNewHighScore = score > currentHighScore;
                if (isNewHighScore) {
                    highScoresJSON[quizUuid] = score;
                    localStorage.setItem(
                        "high_scores",
                        JSON.stringify(highScoresJSON)
                    );
                }

                gameIframeRef.current?.contentWindow?.postMessage(
                    {
                        eventType: "HIGH_SCORE",
                        highScore: highScoresJSON[quizUuid],
                        isNewHighScore,
                    },
                    gameTargetOrigin
                );
            }
            if (event.data?.eventType === "GAME_OVER") {
                setGameState("LOST");
            }
            if (event.data?.eventType === "WIN") {
                setGameState("WON");
            }

            if (
                ["STARTED", "ANSWERED", "FINISHED"].includes(
                    event.data?.eventType
                )
            ) {
                const now = Date.now();
                if (now - lastAnswerTime.current > MIN_ANSWER_INTERVAL) {
                    if (event.data?.eventType === "STARTED") {
                        console.log("STARTED", event.data);
                        setNumQuizQuestions(event.data.numberOfQuestions);
                    }
                    if (event.data?.eventType === "ANSWERED") {
                        console.log("ANSWERED", event.data);
                        setNumQuizQuestionsAnswered((n) => n + 1);
                        const { correct } = event.data;
                        if (correct) {
                            setAmmo((currentAmmo) => currentAmmo + 5);
                        }
                    }
                    lastAnswerTime.current = now;
                    gameIframeRef.current?.contentWindow?.postMessage(
                        event.data,
                        gameTargetOrigin
                    );
                }
            }
        };

        window.addEventListener("message", handleMessage);
        return () => {
            window.removeEventListener("message", handleMessage);
        };
    }, [gameTargetOrigin]);

    useEffect(() => {
        if (showQuizMobile) {
            quizPlayerContainerRef.current?.classList.add("show");
            gameIframeRef.current?.classList.remove("show");
        } else {
            quizPlayerContainerRef.current?.classList.remove("show");
            gameIframeRef.current?.classList.add("show");
        }
    }, [showQuizMobile]);

    const handleQuizPlayAgain = () => {
        // Replay the quiz from the start WITHOUT reloading the page: rebuild the player URL in the
        // current language and re-mount the iframe (key bump), then reset the quiz counters. The
        // tower-defense game keeps running (the quiz is its ammo source), so answering again earns
        // more ammo. No redirect — a teacher trialling the game just replays in the same tab.
        setNumQuizQuestionsAnswered(0);
        setNumQuizQuestions(null);
        setQuizPlayerUrl(getQuizPlayerUrlFromParentUrl(localeRef.current));
        setQuizPlayerKey((k) => k + 1);
        setQuizFinished(false);
    };

    const handleClickResume = () => {
        setGameState("ACTIVE");
        setShowQuizMobile(false);

        gameIframeRef.current?.contentWindow?.postMessage(
            { eventType: "RESUME" },
            gameTargetOrigin
        );
    };

    const handleClickPause = () => {
        setGameState("PAUSED");
        setShowQuizMobile(true);

        gameIframeRef.current?.contentWindow?.postMessage(
            { eventType: "PAUSE" },
            gameTargetOrigin
        );
    };

    const handleClickContinue = (event: React.MouseEvent<HTMLDivElement>) => {
        event.stopPropagation();
        console.log("handleClickContinue");
        setShowQuizMobile(true);
    };

    const handleClickPlayAgain = () => {
        setGameState("ACTIVE");

        gameIframeRef.current?.contentWindow?.postMessage(
            { eventType: "PLAY_AGAIN" },
            gameTargetOrigin
        );
    };

    console.log("gameState", gameState);

    return (
        <div className="App">
            <div className="games">
                <div className="quiz-player" ref={quizPlayerContainerRef}>
                    {quizFinished ? (
                        <div className="endOfQuizOptions">
                            <h2>{t("end.quizComplete")}</h2>

                            <button
                                className="endOfQuizButton playAgain"
                                onClick={handleQuizPlayAgain}
                                type="button"
                            >
                                {t("actions.playAgain")}
                            </button>
                        </div>
                    ) : (
                        <iframe
                            key={quizPlayerKey}
                            id="inlineFrameQuiz"
                            ref={quizPlayerIframeRef}
                            className="iframe"
                            title={t("iframe.quizPlayerTitle")}
                            width="100%"
                            height="100%"
                            src={quizPlayerUrl}
                        />
                    )}
                </div>
                <div
                    className={`game-container ${
                        showQuizMobile ? "mobile-hidden" : ""
                    }`}
                >
                    <iframe
                        id="game"
                        ref={gameIframeRef}
                        className="iframe game"
                        title={t("iframe.gameTitle")}
                        width="100%"
                        height="100%"
                        src={gameFrameUrl}
                    />

                    {gameState === "NOT_STARTED" && (
                        <div className="bottom-bar desktop">
                            <div className="title">
                                {t("intro.startQuiz")}
                            </div>
                            <button
                                className="how-to-play-btn"
                                onClick={() => setShowHowToPlay(true)}
                                type="button"
                            >
                                {t("actions.howToPlay")}
                            </button>
                        </div>
                    )}
                    {["ACTIVE", "PAUSED"].includes(gameState) && (
                        <div className="bottom-bar desktop">
                            <div className="row">
                                <div className="column">
                                    <div className="subtitle">
                                        {t("game.answerForAmmo")}
                                    </div>
                                </div>
                            </div>

                            {gameState === "PAUSED" ? (
                                <button
                                    className={
                                        "resume-btn desktop " +
                                        (ammo < 1 ? " disabled" : "")
                                    }
                                    disabled={ammo < 1}
                                    onClick={handleClickResume}
                                    type="button"
                                >
                                    {t("actions.resumeGame")}
                                </button>
                            ) : (
                                <button
                                    className="resume-btn desktop"
                                    onClick={handleClickPause}
                                    type="button"
                                >
                                    {t("actions.pauseGame")}
                                </button>
                            )}

                            <button
                                className="how-to-play-btn"
                                onClick={() => setShowHowToPlay(true)}
                                type="button"
                            >
                                {t("actions.howToPlay")}
                            </button>
                        </div>
                    )}
                    {gameState === "WON" && (
                        <div className="bottom-bar desktop"></div>
                    )}
                    {gameState === "LOST" && (
                        <div className="bottom-bar desktop">
                            {numQuizQuestionsAnswered <
                                (numQuizQuestions || 0) && (
                                <button
                                    className="resume-btn desktop"
                                    onClick={handleClickPlayAgain}
                                    type="button"
                                >
                                    {t("actions.playAgain")}
                                </button>
                            )}
                        </div>
                    )}
                </div>
                {showHowToPlay && (
                    <div className="modal-bg">
                        <div className="how-to-play-modal">
                            <button
                                className="close-btn"
                                onClick={() => setShowHowToPlay(false)}
                                type="button"
                            >
                                X
                            </button>
                            <div className="how-to-play-title">
                                {t("howToPlay.title")}
                            </div>
                            <div className="how-to-play-content">
                                <p>{t("howToPlay.destroyBalls")}</p>
                                <img
                                    src={howToGif}
                                    className="how-to-gif"
                                    alt={t("howToPlay.imageAlt")}
                                />

                                <p>{t("howToPlay.getAmmo")}</p>
                                <p>{t("howToPlay.winGame")}</p>
                            </div>
                        </div>
                    </div>
                )}

                {showQuizMobile && gameState === "NOT_STARTED" && (
                    <div className="bottom-bar mobile">
                        <div className="column">
                            <div className="subtitle">
                                {t("intro.startQuizMobile")}
                            </div>
                        </div>
                    </div>
                )}
                {showQuizMobile && gameState === "PAUSED" && (
                    <div className="bottom-bar mobile">
                        <div className="row">
                            <div className="title">{t("game.paused")}</div>
                            <div className="column">
                                <div className="ammo">
                                    {t("game.ammo", { count: ammo })}
                                </div>
                                {t("game.answerForAmmoInGame")}
                                <div className="subtitle"></div>
                            </div>
                        </div>
                        <button
                            className={
                                "resume-btn" + (ammo < 1 ? " disabled" : "")
                            }
                            disabled={ammo < 1}
                            onClick={handleClickResume}
                            type="button"
                        >
                            {t("actions.resume")}
                        </button>
                    </div>
                )}
                {showQuizMobile && gameState === "ACTIVE" && (
                    <div className="bottom-bar mobile">
                        <div className="row">
                            <div className="column">
                                <div className="ammo">
                                    {t("game.ammo", { count: ammo })}
                                </div>
                                {t("game.answerForAmmoInGame")}
                                <div className="subtitle"></div>
                            </div>
                        </div>
                        <button
                            className={
                                "resume-btn" + (ammo < 1 ? " disabled" : "")
                            }
                            disabled={ammo < 1}
                            onClick={handleClickResume}
                            type="button"
                        >
                            {t("actions.enterGame")}
                        </button>
                    </div>
                )}
                {!showQuizMobile && gameState === "ACTIVE" && (
                    <div className="bottom-bar mobile">
                        <div className="subtitle">
                            {t("game.answerForAmmo")}
                        </div>
                        {t("game.quizPauseNotice")}
                        <button
                            className="resume-btn"
                            onClick={handleClickPause}
                            type="button"
                        >
                            {t("actions.openQuiz")}
                        </button>
                    </div>
                )}
                {!showQuizMobile && ["WON", "LOST"].includes(gameState) && (
                    <div
                        className="bottom-bar mobile"
                        onClick={handleClickContinue}
                    >
                        <button className="resume-btn" type="button">
                            {t("actions.continue")}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}

export default App;
